import { useRouter } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { useMemo } from 'react'

import type { FilterBarUrlStore } from '@/components/ui/filter-bar-url-context'
import { FilterBarUrlContext } from '@/components/ui/filter-bar-url-context'
import { createUrlKeyRegistry } from '@/components/ui/filter-bar-url-state'
import { logger, LOGGER_EVENTS } from '@/lib/monitoring/logger'

// useFilterBarState（汎用 UI）が URL を読み書きできるよう、router につないで供給する。
// 汎用 UI は TanStack Router へ直接依存できないため、この層で橋渡しする
export function FilterBarUrlProvider({ children }: { children: ReactNode }) {
  const router = useRouter()
  const store = useMemo(
    (): FilterBarUrlStore => ({
      claimKey: createUrlKeyRegistry((key) => {
        // no-console のため logger 経由（PostHog 未初期化の開発環境・story では console.error に出る）
        void logger.error(
          LOGGER_EVENTS.FILTER_BAR,
          `FilterBar の urlKey "${key}" が同時に複数マウントされています。同じ URL パラメータ「${key}」を奪い合うため、一覧ごとに別の urlKey（<一覧名>Filter）にしてください`,
        )
      }),
      read: (key): unknown => Reflect.get(router.state.location.search, key),
      write: (key, value) => {
        void router.navigate({
          to: '.',
          search: (prev: Record<string, unknown>) => ({ ...prev, [key]: value }),
          replace: true,
          resetScroll: false,
        })
      },
      pathname: () => router.state.location.pathname,
      subscribe: (onChange) => router.subscribe('onResolved', onChange),
    }),
    [router],
  )
  return <FilterBarUrlContext value={store}>{children}</FilterBarUrlContext>
}
